import { useEffect, useRef } from 'react';

import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { LineInput } from '@/UI/Input/Input';
import { TextStdRg } from '@/UI/AppTypography/AppTypography';
import { color } from '@/colors';

interface DashboardInlineEditInputProps {
  isEditing: boolean;
  value: string;
  displayValue: string;
  onChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  'data-testid'?: string;
}

const contentStyles = {
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: 1.2,
} as const;

export function DashboardInlineEditInput({
  isEditing,
  value,
  displayValue,
  onChange,
  onConfirm,
  onCancel,
  'data-testid': dataTestId,
}: DashboardInlineEditInputProps) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Focus the input on the next frame rather than synchronously on mount. When rename is triggered
  // via the context menu, the closing menu (FPL MenuV2) returns focus to its trigger (the card) on
  // unmount, which fires after this component mounts. A synchronous focus would be immediately stolen
  // back to the card — blurring the input and firing `onBlur` → `onCancel`. Deferring to a rAF lets
  // the menu's focus-return happen first so this input wins the focus race.
  useEffect(() => {
    if (!isEditing) return;
    const raf = requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [isEditing]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (value.trim()) {
        onConfirm();
      } else {
        onCancel();
      }
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <Box sx={{ py: 0.5, position: 'relative', minHeight: '1.5em' }} data-testid={dataTestId}>
      {/* Typography always in flow to define height; hidden when editing */}
      <TextStdRg
        component="div"
        color={token.text}
        sx={{
          ...contentStyles,
          display: 'block',
          visibility: isEditing ? 'hidden' : 'visible',
        }}
      >
        {isEditing ? value : displayValue}
      </TextStdRg>
      {isEditing && (
        <Box sx={{ position: 'absolute', top: 4, right: 0, bottom: 4, left: 0, ...contentStyles }}>
          <LineInput
            fullWidth
            multiline
            inputRef={(el: HTMLInputElement | HTMLTextAreaElement | null) => {
              inputRef.current = el;
            }}
            onFocus={(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              e.currentTarget.setSelectionRange(0, e.currentTarget.value.length)
            }
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={onCancel}
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
            onMouseUp={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
            sx={{
              width: '100%',
              p: 0,
              ...contentStyles,
              color: 'inherit',
              '&::before': { borderBottomColor: color.White64_T },
              '&::after': { borderBottomColor: color.White64_T },
              '& input, & textarea': {
                ...contentStyles,
                color: 'inherit',
                '&::selection': { backgroundColor: color.TextSelection },
              },
              '& textarea': { resize: 'none' },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
